import React, { useMemo } from 'react';

import { prepareHtmlForPreview } from './sanitize-html';

interface HtmlPreviewProps {
  /**
   * Untrusted HTML (e.g. AI-generated draft or redline content). It is
   * sanitized internally before rendering — pass the raw HTML, not a
   * pre-sanitized string.
   */
  html: string;
  className?: string;
}

/**
 * The single sanctioned place in the app that calls `dangerouslySetInnerHTML`.
 *
 * It always runs the HTML through `prepareHtmlForPreview` (a DOMPurify
 * allowlist) first, so a caller cannot render unsanitized markup. The sanitizer
 * is baked in deliberately — it is NOT an injectable prop, because an injectable
 * sanitizer could be passed a no-op and silently defeat the guarantee. An ESLint
 * `no-restricted-syntax` rule forbids `dangerouslySetInnerHTML` everywhere except
 * this file, so new code renders untrusted HTML by using this component.
 */
export const HtmlPreview = React.memo(function HtmlPreview({ className, html }: HtmlPreviewProps) {
  const sanitizedHtml = useMemo(() => prepareHtmlForPreview(html), [html]);

  return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} className={className} />;
});
